import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { AuthGuardService as AuthGuard } from './auth/auth-guard.service';
import { AuthPassService as AuthPass } from './auth/auth-pass.service';
import { AdminCoachComponent } from './admin/admin-coaches/admin-coaches.component';
import { CreateUserComponent } from './auth/create-user/create-user.component';
import { IS_NEW_UI_FLOW } from '../environments/environment';

const routes: Routes = [
  {
    path: IS_NEW_UI_FLOW ? 'auth' : 'login',
    loadChildren: () => IS_NEW_UI_FLOW ? 
                        import('./auth-private/auth-private.module').then((m) => m.AuthPrivateModule) : 
                        import('./auth/auth.module').then((m) => m.AuthModule),
    title: 'Authentication',
  },
  {
    path: 'join/:id',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./team-join/team-join.module').then(m => m.TeamJoinModule),
    title: 'Join Team'
  },
  {
    path: 'login2',
    loadChildren: () =>
      import('./registration/registration.module').then(
        (m) => m.RegistrationModule
      ),
  },
  {
    path: 'live-scoring',
    canActivate: [AuthPass],
    loadChildren: () =>
      import('./match-tracker/match-tracker.module').then(
        (m) => m.MatchTrackerPageModule
      ),
    title: 'Live score',
  },
  {
    path: 'official-view',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./official-view/official-view.module').then(
        (m) => m.OfficialViewPageModule
      ),
    title: "Official's View",
  },
  {
    path: 'my-coaches',
    component: AdminCoachComponent,
    title: 'Admin | Coaches',
  },
  {
    path: 'ties/:tieId',
    canActivate: [AuthPass],
    loadChildren: () =>
      import('./ties/tie-details/tie-details.module').then(
        (m) => m.TieDetailsPageModule
      ),
    title: 'Team Matches',
  },
  {
    path: 'no-internet',
    loadComponent: () =>
      import('./no-internet/no-internet.component').then(m => m.NoInternetComponent),
    title: 'No Internet'
  },
  {
    path: 'settings',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./settings/settings.module').then((m) => m.SettingsPageModule),
    title: 'Settings',
  },
  {
    path: 'feedback',
    canActivate: [AuthPass],
    loadChildren: () =>
      import('./feedback/feedback.module').then((m) => m.FeedbackPageModule),
    title: 'Feedback',
  },
  {
    path: 'blog',
    loadChildren: () =>
      import('./blog/blog.module').then((m) => m.BlogPageModule),
  },
  {
    path: 'my-profile',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./my-profile/my-profile.module').then(
        (m) => m.MyProfilePageModule
      ),
    title: 'My Profile',
  },
  {
    path: 'my-players/:id',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./player-details/player-details.module').then(
        (m) => m.PlayerDetailsPageModule
      ),
    title: 'My Players',
  },
  {
    path: 'my-players/:id/team/:teamId',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./player-details/player-details.module').then(
        (m) => m.PlayerDetailsPageModule
      ),
    title: 'My Players',
  },
  {
    path: 'my-players',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./my-players/my-players.module').then(
        (m) => m.MyPlayersPageModule
      ),
    title: 'My Players',
  },
  {
    path: 'smart-venues',
    canActivate: [AuthGuard],
    data: { roleMinimumValue: 199 },
    loadChildren: () =>
      import('./venues/venue-routes').then((m) => m.venueRoutes),
    title: 'Smart Venues',
  },
  {
    path: 'my-teams',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./my-teams/my-teams.module').then((m) => m.MyTeamsPageModule),
    title: 'My Teams',
  },
  {
    path: '',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./my-matches/my-matches.module').then(
        (m) => m.MyMatchesPageModule
      ),
  },
  {
    path: 'share',
    canActivate: [AuthPass],
    loadChildren: () =>
      import('./share/share.module').then((m) => m.SharePageModule),
  },
  {
    path: 'protected-share',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./protected-share/protected-share.module').then(
        (m) => m.ProtectedSharePageModule
      ),
  },
  {
    path: 'leagues',
    canActivate: [AuthPass],
    loadChildren: () =>
      import('./leagues/leagues.module').then((m) => m.LeaguesPageModule),
    title: 'Team Events',
  },
  {
    path: 'tournaments',
    canActivate: [AuthPass],
    loadChildren: () =>
      import('./tournaments/tournaments.module').then(
        (m) => m.TournamentsPageModule
      ),
    title: 'Tournaments',
  },
  {
    path: 'home',
    loadChildren: () =>
      import('./website/website.module').then((m) => m.WebsitePageModule),
    title: 'iOnCourt | Share scores in real time',
  },
  {
    path: 'promo',
    loadComponent: () =>
      import('./promo/promo.component').then((m) => m.PromoComponent),
    title: 'iOnCourt | Share scores in real time',
  },
  {
    path: 'admin',
    canActivate: [AuthGuard],
    data: { roleMinimumValue: 399 },
    loadChildren: () =>
      import('./admin/admin.module').then((m) => m.AdminPageModule),
    title: 'Admin',
  },
  {
    path: 'generate-token',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./generate-token/generate-token.module').then((m) => m.GenerateTokenModule),
    title: 'Generate Partner Token',
  },
  {
    path: 'manage-tokens',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./manage-tokens/manage-tokens.module').then((m) => m.ManageTokensModule),
    title: 'Manage Partner Tokens',
  },
  {
    path: 'college-results',
    canActivate: [AuthPass],
    loadChildren: () =>
      import('./search/search.module').then((m) => m.SearchPageModule),
    title: 'College Tennis',
  },
  {
    path: 'search',
    canActivate: [AuthPass],
    loadChildren: () =>
      import('./search/search.module').then((m) => m.SearchPageModule),
  },
  {
    path: 'privacy-policy',
    loadChildren: () =>
      import('./_shared/components/privacy-policy/privacy-policy.module').then(
        (m) => m.PrivacyPolicyModule
      ),
    title: 'Privacy Policy',
  },
  {
    path: 'app',
    loadChildren: () => import('./app.routes').then((m) => m.appRoutes),
    title: 'Privacy Policy',
  },
  {
    path: 'terms-conditions',
    loadChildren: () =>
      import(
        './_shared/components/terms-and-conditions/terms-and-conditions.module'
      ).then((m) => m.TermsAndConditionsModule),
    title: 'Terms & Conditions',
  },
  {
    path: 'video-board-auth',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./video-board-auth/video-board-auth-routes').then(
        (m) => m.videoBoardAuthRoutes
      ),
    title: 'Tv App Authorization',
  },
  {
    path: 'intennse-official-view',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./intennse-official-view/intennse-official-view.module').then(
        (m) => m.TempIntennseViewModule
      ),
    title: "Intennse Official's View",
  },
  { path: '**', redirectTo: '' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule { }
